import { useEffect, useState } from "react";
import { avisosEtapa, salvarAvisoEtapa } from "../services/api";
import { HtmlEditor, WhatsappEditor } from "./RichEditors";

/**
 * Onde a empresa decide o que o candidato fica sabendo, e quando.
 *
 * A regra que organiza esta tela: o texto é DELA. O eaiRH sugere uma tradução para ninguém começar da folha em
 * branco, mas a mensagem sai com o nome da empresa, então quem escreve é quem responde por ela. E tudo nasce
 * desligado — ligar é decisão consciente, não consequência de a integração ter ficado pronta.
 */
export default function AvisosEtapa() {
  const [lista, setLista] = useState(null);
  const [salvando, setSalvando] = useState(null);
  const [sel, setSel] = useState(null);   // etapa aberta para edição
  const [verTodas, setVerTodas] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => { avisosEtapa().then(setLista).catch(() => setLista([])); }, []);

  function mexer(id, campo, valor) {
    setLista((l) => l.map((a) => (a.id === id ? { ...a, [campo]: valor, sujo: true } : a)));
  }

  async function salvar(a) {
    setSalvando(a.id); setErro("");
    try {
      await salvarAvisoEtapa(a.id, { textoCandidato: a.textoCandidato, corpo: a.corpo || "",
        corpoWhatsapp: a.corpoWhatsapp || "", ativo: a.ativo, atrasoMinutos: a.atrasoMinutos });
      setLista((l) => l.map((x) => (x.id === a.id ? { ...x, sujo: false } : x)));
    } catch (e) {
      setErro(e?.message || "Não consegui salvar.");
    } finally { setSalvando(null); }
  }

  if (lista === null) return null;

  // Em vez de sumir: agora isto mora numa aba própria, e aba em branco parece defeito. Explicar de onde vem a
  // lista é o que transforma "não tem nada aqui" em "falta um passo, e é este".
  if (lista.length === 0)
    return (
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900">Avisar o candidato</h2>
        <p className="text-sm text-gray-600 mt-1">
          Quando alguém muda de etapa no seu sistema de recrutamento, o eaiRH pode avisar a pessoa
          automaticamente. As etapas aparecem aqui sozinhas assim que a integração estiver conectada, em
          "Integração" — cada uma com o seu próprio texto, que você edita.
        </p>
      </section>
    );

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="text-lg font-bold text-gray-900">Avisar o candidato</h2>
      <p className="text-sm text-gray-600 mt-1">
        Quando alguém muda de etapa no seu sistema, o eaiRH envia um e-mail avisando a pessoa. Cada etapa tem o
        seu próprio texto, escrito por vocês, e você edita quando quiser. Nada é enviado enquanto você não ligar.
      </p>

      {erro && <p role="alert" className="mt-3 text-sm text-red-600">{erro}</p>}

      {/* RELEVÂNCIA, não lista inteira. São 17 etapas nos dois funis da empresa, e a maioria nunca vai ser
          configurada — mas ESCONDER etapa é esconder gente: se uma delas tiver quatro candidatos parados e não
          aparecer, a tela que deveria mostrar quem espera passa a ocultar. Então nada some de verdade: o que
          avisa e o que tem candidato fica à vista, o resto recolhe atrás de um clique. */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {(verTodas ? lista : lista.filter((a) => a.ativo || a.quantos > 0)).map((a) => (
          <button key={a.id} type="button" onClick={() => setSel(a.id)}
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border ${
              sel === a.id ? "border-primary text-primary bg-primary/5"
              : a.ativo ? "border-green-200 bg-green-50 text-green-800"
              : "border-gray-200 bg-white text-gray-500"}`}>
            {a.ativo ? "● " : "○ "}{a.etapaNome}
          </button>
        ))}
      </div>

      {lista.some((a) => !a.ativo && a.quantos === 0) && (
        <button type="button" onClick={() => setVerTodas((v) => !v)}
          className="mt-2 text-xs font-semibold text-primary hover:underline">
          {verTodas
            ? "Mostrar só o que está em uso"
            : `Ver as outras ${lista.filter((a) => !a.ativo && a.quantos === 0).length} etapas`}
        </button>
      )}

      <label className="block mt-4">
        <span className="text-xs text-gray-600">Etapa</span>
        <select value={sel ?? ""} onChange={(e) => setSel(Number(e.target.value) || null)}
          className="mt-0.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="">Escolha uma etapa para configurar</option>
          {lista.map((a) => (
            <option key={a.id} value={a.id}>
              {a.etapaNome}{a.final ? " (encerra o processo)" : ""} — {a.ativo ? "ligado" : "desligado"} · {a.quantos} aqui hoje
            </option>
          ))}
        </select>
      </label>

      <div className="mt-4">
        {lista.filter((a) => a.id === sel).map((a) => (
          <div key={a.id} className={`rounded-xl border p-3 ${a.ativo ? "border-green-200 bg-green-50/40" : "border-gray-200"}`}>
            <div className="flex flex-wrap items-center gap-2">
              {/* O nome CRU da etapa é o que ela reconhece do próprio sistema. Traduzir aqui faria a pessoa
                  procurar na tela dela uma coluna com outro nome. */}
              <span className="font-semibold text-sm text-gray-900">{a.etapaNome}</span>
              {a.final && <span className="rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-[11px] font-semibold">encerra o processo</span>}
              <span className="text-[11px] text-gray-500">{a.quantos} candidato(s) aqui hoje</span>

              {/* Rótulo no POSITIVO e descrevendo a AÇÃO, não o estado. Antes dizia "não avisa" ao lado de uma
                  caixa desmarcada: marcar LIGA o aviso, mas o texto negativo fazia parecer que marcar desligaria.
                  Numa tela onde o engano manda e-mail para pessoa real, isso não é detalhe de redação. */}
              <label className="ml-auto flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={a.ativo} onChange={(e) => mexer(a.id, "ativo", e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary" />
                <span className="text-gray-700">Avisar quem chegar nesta etapa</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${a.ativo ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}`}>
                  {a.ativo ? "ligado" : "desligado"}
                </span>
              </label>
            </div>

            {/* Este campo nasceu antes dos editores existirem, quando era o único texto que havia. Hoje ele é
                RESERVA: só é usado se a mensagem de e-mail ficar vazia. Um campo que não diz para que serve faz
                quem configura parar e tentar adivinhar — e o rótulo antigo ("Resumo") não dizia nada. */}
            <label className="block mt-2">
              <span className="text-xs text-gray-600">Como esta etapa aparece para o candidato</span>
              <input value={a.textoCandidato} maxLength={400}
                onChange={(e) => mexer(a.id, "textoCandidato", e.target.value)}
                className="mt-0.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <p className="text-[11px] text-gray-400 mt-1">
                É o que a pessoa lê em "minhas candidaturas" — o nome interno da etapa nunca aparece para ela.
                Também é usada no e-mail se você deixar a mensagem abaixo em branco.
              </p>
            </label>

            {/* Editor de verdade, o MESMO dos modelos de encerramento. Uma linha seca assinada pela empresa
                comunica o fato e não comunica cuidado — e cuidado é o que estamos vendendo. */}
            <div className="mt-3">
              <span className="text-xs text-gray-600">Mensagem por e-mail</span>
              {/* initialHtml, NÃO value: o editor só lê o conteúdo na montagem. Passar o nome errado fazia ele
                  nascer vazio mesmo com o texto gravado — o modelo existia no banco e a tela mostrava um campo em
                  branco. E o `key` força remontar ao trocar de etapa; sem ele, o editor continuaria exibindo o
                  texto da etapa anterior, que é pior que vazio: a pessoa salvaria o texto errado achando que é o
                  daquela etapa. */}
              <HtmlEditor key={`html-${a.id}`} initialHtml={a.corpo || ""}
                onChange={(v) => mexer(a.id, "corpo", v)} />
              <p className="text-[11px] text-gray-400 mt-1">
                Use <b>{"{nome}"}</b>, <b>{"{vaga}"}</b> e <b>{"{empresa}"}</b> — trocamos pelos dados de quem
                vai receber. Em branco, enviamos só o resumo acima.
              </p>
            </div>

            <div className="mt-3">
              <span className="text-xs text-gray-600">Mensagem por WhatsApp</span>
              <WhatsappEditor value={a.corpoWhatsapp || ""} onChange={(v) => mexer(a.id, "corpoWhatsapp", v)}
                placeholder="Versão curta, com *negrito* e emoji. Em branco, derivamos do e-mail." />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-600">Enviar</span>
              <select value={a.atrasoMinutos} onChange={(e) => mexer(a.id, "atrasoMinutos", Number(e.target.value))}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
                <option value={0}>na hora</option>
                <option value={60}>depois de 1 hora</option>
                <option value={240}>depois de 4 horas</option>
                <option value={1440}>depois de 1 dia</option>
                <option value={2880}>depois de 2 dias</option>
              </select>

              {a.sujo && (
                <button type="button" onClick={() => salvar(a)} disabled={salvando === a.id}
                  className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60">
                  {salvando === a.id ? "Salvando..." : "Salvar"}
                </button>
              )}
            </div>

            {/* Etapa sem modelo não é esquecimento nosso: é informação interna da empresa que não diz respeito
                ao candidato. Dizer isso aqui evita que alguém escreva um texto em "Perdidas" — que significa que
                a empresa perdeu o cliente — achando que faltou preencher. */}
            {!a.corpo && !a.corpoWhatsapp && (
              <p className="mt-2 text-xs text-gray-600 bg-gray-50 rounded-lg px-2.5 py-2">
                Esta etapa não vem com texto pronto porque costuma ser de uso interno e não diz respeito ao
                candidato. Se fizer sentido avisar alguém aqui, escreva a mensagem acima e ligue o aviso.
              </p>
            )}

            {/* A explicação fica ONDE a decisão é tomada, não num manual. Quem configura "Descartados" precisa
                saber, naquele instante, por que a espera existe — senão deixa em "na hora" achando que rapidez
                é sempre gentileza. */}
            {a.final && a.atrasoMinutos === 0 && (
              <p className="mt-2 text-xs text-amber-800 bg-amber-50 rounded-lg px-2.5 py-2">
                Ninguém gosta de receber uma negativa segundos depois de se candidatar. Quando a triagem é rápida,
                o "não" imediato passa a impressão de que ninguém olhou o currículo. Uma espera não muda a
                resposta, muda só o relógio.
              </p>
            )}
          </div>
        ))}
        {!sel && (
          <p className="text-sm text-gray-500">
            Escolha uma etapa acima para escrever a mensagem. As que estão com <b>●</b> já avisam o candidato.
          </p>
        )}
      </div>

    </section>
  );
}
